import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import KeyboardOutlinedIcon from '@mui/icons-material/KeyboardOutlined'
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined'
import React, { FormEvent, useEffect, useRef, useState } from 'react'
import BaseButton from './buttons/BaseButton'

interface TextAnswererProps {
  handleChange: (t: string) => void
  sendResponse: () => void
  textValue?: string
}

const TextAnswerer: React.FC<TextAnswererProps> = ({ handleChange, sendResponse, textValue }) => {
  const [isInputVisible, setIsInputVisible] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const measuringDivRef = useRef<HTMLDivElement>(null)

  const locale = localStorage.getItem('locale')

  const handleKeyboardClick = (): void => setIsInputVisible(true)
  const handleMicClick = (): void => setIsInputVisible(false)

  const calculateTextHeight = (): void => {
    if (!textareaRef.current || !measuringDivRef.current) return

    const textarea = textareaRef.current
    const measuringDiv = measuringDivRef.current

    const baseHeight = 40

    if (textarea.value === '') {
      textarea.style.height = `${baseHeight}px`
      return
    }

    // Ajoute une espace non sécable (\u00A0) ou un caractère invisible à la fin du texte
    // dans le measuringDiv pour forcer un recalcul plus fidèle, même avec une ligne vide
    measuringDiv.textContent = (textarea.value || ' ') + '\u200B'

    const measuredHeight = measuringDiv.scrollHeight + 20

    const newHeight = Math.max(baseHeight, Math.min(measuredHeight, 104))
    textarea.style.height = `${newHeight}px`
  }

  const setupMeasuringDiv = (): void => {
    if (!textareaRef.current || !measuringDivRef.current) return

    const textarea = textareaRef.current
    const measuringDiv = measuringDivRef.current
    const computedStyle = window.getComputedStyle(textarea)


    // Nécessaire pour forcer la manipulation du dom en temps réels avec le textarea
    measuringDiv.style.font = computedStyle.font
    measuringDiv.style.fontFamily = computedStyle.fontFamily
    measuringDiv.style.fontSize = computedStyle.fontSize
    measuringDiv.style.fontWeight = computedStyle.fontWeight
    measuringDiv.style.lineHeight = computedStyle.lineHeight
    measuringDiv.style.letterSpacing = computedStyle.letterSpacing
    measuringDiv.style.wordSpacing = computedStyle.wordSpacing
    measuringDiv.style.textTransform = computedStyle.textTransform
    measuringDiv.style.whiteSpace = 'pre-wrap'
    measuringDiv.style.wordWrap = 'break-word'
    measuringDiv.style.width = `${textarea.clientWidth - 20}px`
    measuringDiv.style.padding = '0'
    measuringDiv.style.margin = '0'
  }

  useEffect(() => {
    if (isInputVisible) {
      setTimeout(() => {
        setupMeasuringDiv()
        calculateTextHeight()
      }, 0)
    }
  }, [isInputVisible])

  useEffect(() => {
    calculateTextHeight()
  }, [textValue])

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key === 'Enter') {
      if (event.shiftKey) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            calculateTextHeight()
          })
        })
      } else {
        event.preventDefault()
        if (
          event.currentTarget.value &&
          event.currentTarget.value.trim() !== '' &&
          event.currentTarget.value?.length > 0
        ) {
          sendResponse()
        }
      }
    }
  }

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    sendResponse()
  }

  const handleTextChange = (value: string): void => {
    handleChange(value)
    // Double requestAnimationFrame pour s'assurer que le DOM est à jour
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        calculateTextHeight()
      })
    })
  }

  const handleResize = (): void => {
    setupMeasuringDiv()
    calculateTextHeight()
  }

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    return (): void => window.removeEventListener('resize', handleResize)
  }, [])

  return (
    <div className="keyboard-input-container">
      {!isInputVisible ? (
        <BaseButton className="keyboard-icon" onClick={handleKeyboardClick}>
          <KeyboardOutlinedIcon />
        </BaseButton>
      ) : (
        <div className="open-keyboard">
          <form onSubmit={handleSubmit} className="input-section">
            <BaseButton className="mic-icon" onClick={handleMicClick}>
              <RecordVoiceOverOutlinedIcon />
            </BaseButton>

            {/* Div invisible pour mesurer la hauteur du texte */}
            <div
              ref={measuringDivRef}
              style={{
                position: 'absolute',
                visibility: 'hidden',
                height: 'auto',
                width: 'auto',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word'
              }}
            />

            <textarea
              ref={textareaRef}
              autoFocus
              className="text-input"
              onKeyDown={handleKeyDown}
              onInput={calculateTextHeight}
              placeholder={locale == 'en' ? 'Your text here...' : 'Saisissez votre réponse ici...'}
              value={textValue ?? ''}
              onChange={e => handleTextChange(e.target.value)}
              style={{
                height: '40px',
                maxHeight: '104px',
                overflowY: 'hidden',
                resize: 'none',
                padding: '8px',
                border: '1px solid #ccc',
                outline: 'none',
                lineHeight: '20px',
                boxSizing: 'border-box'
              }}
            />

            <BaseButton className="send-button" type="submit">
              <ChevronRightIcon />
            </BaseButton>
          </form>
        </div>
      )}
    </div>
  )
}

export default TextAnswerer
